import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Grid, Typography } from "@mui/material";
import type { SxProps } from "@mui/material";
import { elusUtil } from "utils";
import type { SdeappsError } from "utils/errorHandling";
import type { Mandat, Personne } from "models";
import CommuneLink from "components/Links/CommuneLink";
import PerimetreLinkWithErrorBoundary from "components/Links/PerimetreLink";
import TerritoireLink from "components/Links/TerritoireLink";
import CollectiviteLinkInstitutionnelMandat from "components/Informations/Elus/CollectiviteLinkInstitutionnelMandat";
import InlineErrorChip from "components/Errors/InlineErrorChip";
import TypographySkeleton from "components/Links/TypographySkeleton";

interface SdeaMandatCollectiviteLinkProps {
  mandatSdea: Mandat;
  noLink: boolean;
  prefix?: string;
}

function SdeaMandatCollectiviteLink({
  mandatSdea,
  noLink,
  prefix,
}: Readonly<SdeaMandatCollectiviteLinkProps>): ReactNode {
  if (mandatSdea.codeCollectivite != null) {
    return (
      <>
        {prefix}
        <CommuneLink
          id={mandatSdea.codeCollectivite}
          libelle={mandatSdea.libelleCollectivite}
          noLink={noLink}
        />
      </>
    );
  } else if (mandatSdea.idPerimetre != null && mandatSdea.competence != null) {
    return (
      <>
        {prefix}
        <PerimetreLinkWithErrorBoundary
          id={mandatSdea.idPerimetre}
          libelle={mandatSdea.libellePerimetre}
          noLink={noLink}
        />
      </>
    );
  } else if (mandatSdea.idTerritoire != null) {
    return (
      <>
        {prefix}
        <TerritoireLink
          id={mandatSdea.idTerritoire}
          libelle={mandatSdea.libelleTerritoire}
          noLink={noLink}
        />
      </>
    );
  } else return null;
}

interface HigherMandatsSubtitleProps {
  elu?: Personne;
  sx?: SxProps;
  isLoading?: boolean;
  error?: SdeappsError;
  noLink?: boolean;
}

function HigherMandatsSubtitle({
  elu,
  sx,
  isLoading = false,
  error,
  noLink = false,
}: Readonly<HigherMandatsSubtitleProps>): ReactNode {
  const [higherSdeaMandat, setHigherSdeaMandat] = useState<Mandat>();
  const [higherInstitutionnelMandat, setHigherInstitutionnelMandat] = useState<Mandat>();

  useEffect(() => {
    setHigherSdeaMandat(undefined);
    setHigherInstitutionnelMandat(undefined);
    if (elu != null) {
      const activeElus = elusUtil.getActiveElusAndMandats([elu]);

      if (activeElus.length > 0) {
        setHigherSdeaMandat(elusUtil.getHigherSdeaMandat(activeElus[0]));
        setHigherInstitutionnelMandat(elusUtil.getHigherInstitutionnelMandat(activeElus[0]));
      }
    }
  }, [elu]);

  if (error != null) {
    return <InlineErrorChip />;
  }

  if (isLoading || elu == null) {
    return <TypographySkeleton width="100%" />;
  }

  if (higherSdeaMandat == null && higherInstitutionnelMandat == null) return null;

  return (
    <Grid
      container
      size={12}
      sx={{
        gap: 1,
        backgroundColor: "info.light",
        padding: 1,
        borderRadius: 1,
        ...sx,
      }}>
      {higherSdeaMandat != null && (
        <Grid size={12}>
          <Typography>
            {elusUtil.getLibelleFonction(higherSdeaMandat)}
            <SdeaMandatCollectiviteLink
              mandatSdea={higherSdeaMandat}
              noLink={noLink}
              prefix=" - "
            />
          </Typography>
        </Grid>
      )}
      {higherInstitutionnelMandat != null && (
        <Grid size={12}>
          <Typography>
            {elusUtil.getLibelleFonction(higherInstitutionnelMandat)} {" - "}
            <CollectiviteLinkInstitutionnelMandat
              mandatInstitutionnel={higherInstitutionnelMandat}
              noLink={noLink}
            />
          </Typography>
        </Grid>
      )}
    </Grid>
  );
}

export default HigherMandatsSubtitle;
